import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Table, Form, Button, Modal } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { formatDate } from '../utils/DateUtils';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { IconButton } from '@mui/material';

const TeamDetails = () => {
    const { teamId } = useParams();
    const [teamDetails, setTeamDetails] = useState(null);
    const [newPlayer, setNewPlayer] = useState({ first_name: '', last_name: '', birth_date: '' });
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingPlayer, setEditingPlayer] = useState(null);
    const { isAuthenticated, checkAuth } = useAuth();

    useEffect(() => {
        const fetchTeamDetails = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/details/team/${teamId}`);
                setTeamDetails(response.data);
            } catch (error) {
                console.error(error);
            }
        };

        fetchTeamDetails();
        checkAuth(); // Call checkAuth only once when the component mounts
    }, [teamId, checkAuth]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewPlayer(prev => ({ ...prev, [name]: value }));
    };

    const handleAddPlayer = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                team_id: teamId,
                first_name: newPlayer.first_name,
                last_name: newPlayer.last_name,
                birth_date: newPlayer.birth_date || null
            };
            const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/player/add`, payload, {
                withCredentials: true
            });
            setTeamDetails(prevDetails => ({
                ...prevDetails,
                players: [...prevDetails.players, response.data]
            }));
            setNewPlayer({ first_name: '', last_name: '', birth_date: '' });
            setShowAddModal(false);
        } catch (error) {
            console.error(error);
        }
    };

    const openEditModal = (player) => {
        setEditingPlayer({
            id: player.id,
            first_name: player.first_name,
            last_name: player.last_name,
            birth_date: player.birth_date || null
        });
        setShowEditModal(true);
    };

    const handleEditInputChange = (e) => {
        const { name, value } = e.target;
        setEditingPlayer(prev => ({ ...prev, [name]: value }));
    };

    const handleEditPlayer = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                id: editingPlayer.id,
                first_name: editingPlayer.first_name,
                last_name: editingPlayer.last_name,
                birth_date: editingPlayer.birth_date || null
            };
            await axios.put(`${process.env.REACT_APP_BACKEND_URL}/player/update/${editingPlayer.id}`, payload, {
                withCredentials: true
            });
            setTeamDetails(prevDetails => ({
                ...prevDetails,
                players: prevDetails.players.map(p =>
                    p.id === editingPlayer.id
                        ? { ...p, first_name: payload.first_name, last_name: payload.last_name, birth_date: payload.birth_date }
                        : p
                )
            }));
            setShowEditModal(false);
            setEditingPlayer(null);
        } catch (error) {
            console.error(error);
        }
    };

    const handleDeletePlayer = async (playerId) => {
        try {
            await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/player/delete/${playerId}`, {
                withCredentials: true
            });
            setTeamDetails(prevDetails => ({
                ...prevDetails,
                players: prevDetails.players.filter(player => player.id !== playerId)
            }));
        } catch (error) {
            console.error(error);
            const player = teamDetails.players.find(player => player.id === playerId);
            setError(`Failed to delete player: ${player.first_name} ${player.last_name}`);
            setShowModal(true);
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setError(null);
    };

    if (!teamDetails) {
        return <div>Ładowanie...</div>;
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <h1 style={{ textAlign: 'center', marginTop: '20px', marginBottom: '16px' }}>{teamDetails.team_name}</h1>
            {teamDetails.logo_filename && (
                <img
                    src={`/logos/${teamDetails.logo_filename}`}
                    alt={teamDetails.team_name}
                    style={{ height: '300px', objectFit: 'contain', marginBottom: '24px' }}
                    onError={(e) => (e.currentTarget.style.display = 'none')}
                />
            )}

            <h3>Zawodnicy</h3>
            {isAuthenticated && (
                <div className="text-center mb-3">
                    <Button variant="primary" onClick={() => setShowAddModal(true)}>
                        Dodaj zawodnika
                    </Button>
                </div>
            )}
            <Table striped bordered hover>
                <thead>
                    <tr>
                        <th>Imię</th>
                        <th>Nazwisko</th>
                        <th>Data urodzenia</th>
                        {isAuthenticated && <th>Akcje</th>}
                    </tr>
                </thead>
                <tbody>
                    {(teamDetails.players ?? []).map((player, index) => (
                        <tr key={index}>
                            <td>{player.first_name}</td>
                            <td>{player.last_name}</td>
                            <td>{player.birth_date ? formatDate(player.birth_date) : ''}</td>
                            {isAuthenticated && (
                                <td>
                                    <IconButton size="small" onClick={() => openEditModal(player)}>
                                        <EditNoteIcon fontSize="small" />
                                    </IconButton>
                                    <Button
                                        variant="danger"
                                        size="sm"
                                        onClick={() => handleDeletePlayer(player.id)}
                                    >
                                        &times;
                                    </Button>
                                </td>
                            )}
                        </tr>
                    ))}
                </tbody>
            </Table>

            <Modal show={showAddModal} onHide={() => { setShowAddModal(false); setNewPlayer({ first_name: '', last_name: '', birth_date: '' }); }}>
                <Modal.Header closeButton>
                    <Modal.Title>Dodaj zawodnika</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleAddPlayer}>
                    <Modal.Body>
                        <Form.Group className="mb-3" controlId="formFirstName">
                            <Form.Label>Imię</Form.Label>
                            <Form.Control
                                type="text"
                                name="first_name"
                                value={newPlayer.first_name}
                                onChange={handleInputChange}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="formLastName">
                            <Form.Label>Nazwisko</Form.Label>
                            <Form.Control
                                type="text"
                                name="last_name"
                                value={newPlayer.last_name}
                                onChange={handleInputChange}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="formBirthDate">
                            <Form.Label>Data urodzenia</Form.Label>
                            <Form.Control
                                type="date"
                                name="birth_date"
                                value={newPlayer.birth_date}
                                onChange={handleInputChange}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => { setShowAddModal(false); setNewPlayer({ first_name: '', last_name: '', birth_date: '' }); }}>
                            Anuluj
                        </Button>
                        <Button variant="primary" type="submit">
                            Dodaj
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            <Modal show={showEditModal} onHide={() => { setShowEditModal(false); setEditingPlayer(null); }}>
                <Modal.Header closeButton>
                    <Modal.Title>Edytuj zawodnika</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleEditPlayer}>
                    <Modal.Body>
                        <Form.Group className="mb-3" controlId="editFirstName">
                            <Form.Label>Imię</Form.Label>
                            <Form.Control
                                type="text"
                                name="first_name"
                                value={editingPlayer?.first_name || ''}
                                onChange={handleEditInputChange}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="editLastName">
                            <Form.Label>Nazwisko</Form.Label>
                            <Form.Control
                                type="text"
                                name="last_name"
                                value={editingPlayer?.last_name || ''}
                                onChange={handleEditInputChange}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="editBirthDate">
                            <Form.Label>Data urodzenia</Form.Label>
                            <Form.Control
                                type="date"
                                name="birth_date"
                                value={editingPlayer?.birth_date || ''}
                                onChange={handleEditInputChange}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => { setShowEditModal(false); setEditingPlayer(null); }}>
                            Anuluj
                        </Button>
                        <Button variant="primary" type="submit">
                            Zapisz
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            <Modal show={showModal} onHide={handleCloseModal}>
                <Modal.Header closeButton>
                    <Modal.Title>Błąd</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>{error}</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseModal}>
                        Zamknij
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default TeamDetails;